import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { LoadStatus } from '../../constants/constants';
import api from '../../services/DataLayer';
import { updateActivities } from './frameworkReducer';

const initialState = {
  activities: [],
  currentlySelectedId: '',
  activityLoadStatus: LoadStatus.INITIAL,
  selectedActivity: {},
};

export const ActivityType = Object.freeze({
  GENERAL: 'general',
  IMPLEMENT: 'implement',
  CORRECTIVE_ACTION: 'correctiveAction',
  RECURRING_OPERATIONAL_ACTIVITY: 'recurringOperationalActivity',
  list() {
    return [this.GENERAL, this.IMPLEMENT, this.RECURRING_OPERATIONAL_ACTIVITY];
  },
});

export const ActivityStatus = Object.freeze({
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'inProgress',
  IN_REVIEW: 'inReview',
  COMPLETED: 'completed',
  list() {
    return [this.NOT_STARTED, this.IN_PROGRESS, this.IN_REVIEW, this.COMPLETED];
  },
});

export const ActivityRepeatInterval = Object.freeze({
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
  list() {
    return [this.MONTH, this.QUARTER, this.YEAR];
  },
});

export const initializeByOrganization = createAsyncThunk(
  'activities/initializeByOrganization',
  async (organizationId, { getState }) => {
    if (getState().activities.currentlySelectedId !== organizationId) {
      let activities = [];
      try {
        activities = await api.getOrganizationActivities(organizationId);
      } catch (e) {
        // TODO: 404
      }
      return { activities, id: organizationId };
    }
    return undefined;
  },
);

export const saveActivity = createAsyncThunk(
  'activities/saveActivity',
  async (activityFormData, { getState, dispatch }) => {
    const activityData = {
      activityStatus: activityFormData.activityStatus,
      activityType: activityFormData.activityType,
      activityOwner: activityFormData.activityOwner,
      autoResolve: activityFormData.autoResolve,
      controlIds: activityFormData.controlIds,
      created: activityFormData.created,
      description: activityFormData.description,
      deadline: activityFormData.deadline,
      edited: activityFormData.edited,
      frameworkId: activityFormData.frameworkId,
      findingId: activityFormData.findingId,
      id: activityFormData.id,
      notes: activityFormData.notes,
      parentId: activityFormData.parentId || getState().organizations.selectedOrganization.id,
      recurringActivity: activityFormData.recurringActivity,
      repeatEvery: activityFormData.repeatEvery,
      title: activityFormData.title,
    };
    if (activityData.id) {
      const _ = await api.updateActivity(getState().organizations.selectedOrganization.id, activityData);
    } else {
      const activityId = await api.createActivity(getState().organizations.selectedOrganization.id, activityData);
      activityData.id = activityId;
    }
    dispatch(updateActivities(activityData));
    return activityData;
  },
);

export const activitySlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    clearSelectedActivity: (state) => {
      state.selectedActivity = {};
      state.activityLoadStatus = LoadStatus.SUCCESS;
    },
    selectActivity: (state, action) => {
      state.selectedActivity = state.activities.find((act) => act.id === action.payload);
      state.activityLoadStatus = LoadStatus.SUCCESS;
    },
    setLoadInProgress: (state, _) => {
      state.activityLoadStatus = LoadStatus.IN_PROGRESS;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeByOrganization.pending, (state, _) => {
        state.activityLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(initializeByOrganization.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.currentlySelectedId = action.payload.id;
          state.activities = action.payload.activities;
        }
        state.activityLoadStatus = LoadStatus.SUCCESS;
      })
      .addCase(saveActivity.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const index = state.activities.findIndex((act) => act.id === action.payload.id);
          const newState = [...state.activities];
          if (index >= 0) {
            newState[index] = action.payload;
          } else {
            newState.unshift(action.payload);
          }
          state.activities = newState;
        }
      })
      .addCase('resetState', (_, __) => ({ ...initialState }));
  },
});

const { actions, reducer } = activitySlice;

export const { clearSelectedActivity, selectActivity, setLoadInProgress } = actions;

export default reducer;

export const selectActivityList = (state) => state.activities.activities;
export const selectSelectedActivity = (state) => state.activities.selectedActivity;
export const selectActivityLoadStatus = (state) => state.activities.activityLoadStatus;
export const selectControlActivityList = createSelector(
  [selectActivityList, (_, controlId) => controlId],
  (activityList, controlId) => activityList.filter((act) => act.controlId === controlId),
);
const selectedFrameworkId = (state) => state.frameworks.selectedFrameworkId;
export const selectFrameworkControlActivityList = createSelector(
  [selectActivityList, selectedFrameworkId, (_, groupId) => groupId],
  (activities, frameworkId, groupId) =>
    activities.filter((act) => act.frameworkId === frameworkId && act.controlIds.includes(groupId)),
);
export const selectFindingActivityList = createSelector(
  [selectActivityList, (_, findingId) => findingId],
  (activityList, findingId) => activityList.filter((act) => act.findingId === findingId),
);
export const selectOneTimeActivities = createSelector(selectActivityList, (activityList) =>
  activityList.filter((act) => act.recurringActivity === false),
);
export const selectRecurringActivities = createSelector(selectActivityList, (activityList) =>
  activityList.filter((act) => act.recurringActivity === true),
);
export const selectFilteredActivities = createSelector(
  [selectActivityList, (_, filterParams) => filterParams],
  (activityList, filterParams) => activityList.filter((act) => act[filterParams.name] === filterParams.value),
);
